import { doesWorkflowRejectCodeExist, getWorkflowRejectCodeMessage } from '@tassat/common-definitions';
import { IFormValues, SubmitPairApiConnectionOpts } from '@tassat/forms/src/PairApiConnectionForm/types';

import encryptDecrypt from 'Lib/encryptDecryptToken';
import Worker from 'Lib/worker/PrimusWorker';

const getUserApiKeyCredentials = async (userApiKeyString: string) => {
    const apiPairKeys = await encryptDecrypt.generateAsymetricKeyPair({
        name: 'ECDSA',
        namedCurve: 'P-256'
    }, ['sign']);

    const userApiKey = encryptDecrypt.createHash(userApiKeyString, 'sha256');

    return {
        apiPairKeys,
        userApiKey
    };
};

export type ApiConnectionUserCredentials = {
    nonce: string;
    apiPairKeys: {
        publicKey: string;
        privateKey: string;
    };
    userApiKey: string;
};

const getSecondaryApiConnectionData = async (opts: SubmitPairApiConnectionOpts):
Promise<ApiConnectionUserCredentials> => {
    const { user: { UserId, Username }} = opts;
    const nonce = Date.now()
        .toString();
    const userCredentials = await getUserApiKeyCredentials(`${Username}.${UserId}.${nonce}`);

    return {
        ...userCredentials,
        nonce
    };
};

export const submitPairApiConnectionForm = async (
    values: IFormValues,
    opts: SubmitPairApiConnectionOpts
) => {
    const {
        onClose,
        setSubmitError,
        setUserCredentials,
        apiConnectionUsers,
        user
    } = opts;
    try {
        let dataToSend: any = {
            UserId: user.UserId,
            ClientId: values.apiClientId
        };
        const isSecondaryApiConnection = values.ApiConnection?.value && values.ApiConnection.value !== '0';
        let credentials;

        if (isSecondaryApiConnection) {
            credentials = await getSecondaryApiConnectionData(opts);
            const secondaryApiConnection = apiConnectionUsers.find(
                (api) => api.apiConnection.APISecondaryConnectionType === values.ApiConnection.value
            );
            dataToSend = {
                ...dataToSend,
                PublicKey: credentials.apiPairKeys.publicKey,
                ApiKey: credentials.userApiKey,
                APIConnectionId: secondaryApiConnection?.apiConnection && secondaryApiConnection.apiConnection.APIConnectionId,
                APISecondaryConnectionType: values?.ApiConnection && values.ApiConnection.value,
                Nonce: credentials.nonce
            };
            delete dataToSend.ClientId;
        }

        const response = await Worker.getInstance()
            .createAPIConnectionUser(dataToSend);

        if (response.data && response.data.Status === 48) {
            if (isSecondaryApiConnection) {
                setUserCredentials(credentials);
            }
            if (onClose) {
                onClose();
            }
        } else if (response.data && response.data.Status === 49) {
            setSubmitError('Failed to pair API');
        } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
            setSubmitError(`Could not pair API: ${getWorkflowRejectCodeMessage(response.data.Status)}`);
        }
    } catch (error) {
        console.error(error);
        setSubmitError('Failed to pair API');
    }
};